import { computed } from 'vue';
import { useDialog } from '@negar/core';
import { PortfolioItem, VirtualPortfolioItem } from '@libs/api-reference';

import {
  EditNetAveragePriceDialog,
  CreateVirtualPortfolioForm,
  EditVirtualPortfolioForm,
  ConfirmDeleteDialog,
  NetAveragePriceInfoDialog,
  TotalBuyInfoDialog,
  DividendDialog,
  BonusShareDialog,
} from '@/modules/portfolio';

import { i18n } from '@/plugins/i18n';

const { t } = i18n.global;

export default function usePortfolioDialogs() {
  const { add: addDialog } = useDialog();

  const openEditNetAveragePriceDialog = ({
    selectedSymbol,
    referenceElementRef,
  }: {
    selectedSymbol: PortfolioItem;
    referenceElementRef?: HTMLElement;
  }) => {
    addDialog({
      title: `${t('modules.portfolio.editNetAveragePrice')}
    ${selectedSymbol.instrumentName}`,
      multiple: true,
      referenceElementRef,
      placement: 'top',
      bodyClass: '!w-[300px]',
      content: computed(() => ({
        component: EditNetAveragePriceDialog,
        props: {
          selectedSymbol,
        },
      })),
    });
  };

  const openEditVirtualPortfolioNetAveragePriceDialog = ({
    selectedSymbol,
    referenceElementRef,
  }: {
    selectedSymbol: VirtualPortfolioItem;
    referenceElementRef?: HTMLElement;
  }) => {
    addDialog({
      title: `${t('common.edit')}
    ${selectedSymbol.instrumentName}`,
      multiple: true,
      referenceElementRef,
      placement: 'bottom',
      content: computed(() => ({
        component: EditVirtualPortfolioForm,
        props: {
          selectedSymbol,
        },
      })),
    });
  };

  const openCreateVirtualPortfolioItemDialog = () => {
    addDialog({
      title: `${t('modules.portfolio.virtualPortfolio.addDialogTitle')}`,
      content: computed(() => ({
        component: CreateVirtualPortfolioForm,
        props: {},
      })),
    });
  };

  const openDeleteVirtualPortfolioItemDialog = ({
    selectedSymbol,
    onDelete,
    referenceElementRef,
    loading,
  }: {
    selectedSymbol: VirtualPortfolioItem;
    onDelete: () => void;
    loading: boolean;
    referenceElementRef?: HTMLElement;
  }) => {
    addDialog({
      title: `${t('misc.delete')}
    ${selectedSymbol.instrumentName}`,
      referenceElementRef,
      bodyClass: '!w-80',
      content: computed(() => ({
        component: ConfirmDeleteDialog,
        props: {
          confirmMessage: t(
            'modules.portfolio.virtualPortfolio.deleteMessage',
            { name: selectedSymbol.instrumentName }
          ),
          onDelete,
          loading,
        },
      })),
    });
  };

  const openNetAveragePriceInfoDialog = () => {
    addDialog({
      title: `${t('modules.portfolio.net_average')}`,
      bodyClass: '!w-[375px]',
      content: computed(() => ({
        component: NetAveragePriceInfoDialog,
        props: {},
      })),
    });
  };

  const openTotalBuyInfoDialog = () => {
    addDialog({
      title: `${t('modules.portfolio.total_buy')}`,
      bodyClass: '!w-[375px]',
      content: computed(() => ({
        component: TotalBuyInfoDialog,
        props: {},
      })),
    });
  };

  const openDividendDialog = ({
    selectedSymbol,
    referenceElementRef,
  }: {
    selectedSymbol: PortfolioItem;
    referenceElementRef?: HTMLElement;
  }) => {
    addDialog({
      title: `${t('modules.portfolio.dividend')}
    ${selectedSymbol.instrumentName}`,
      referenceElementRef,
      placement: 'top',
      bodyClass: '!w-[375px]',
      content: computed(() => ({
        component: DividendDialog,
        props: { selectedSymbol },
      })),
    });
  };

  function openBonusShareDialog(symbolName: string | null, quantity?: number) {
    addDialog({
      title: t('modules.portfolio.bonusShareDialogTitle'),
      bodyClass: '!w-[375px]',
      content: {
        component: BonusShareDialog,
        props: {
          symbolName,
          quantity,
        },
      },
    });
  }

  return {
    openEditNetAveragePriceDialog,
    openEditVirtualPortfolioNetAveragePriceDialog,
    openCreateVirtualPortfolioItemDialog,
    openDeleteVirtualPortfolioItemDialog,
    openNetAveragePriceInfoDialog,
    openTotalBuyInfoDialog,
    openDividendDialog,
    openBonusShareDialog,
  };
}
